import "./narrative.scss";

import { List } from "immutable";
import _ from "lodash";
import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React, { PureComponent } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";

import { withContainerContext } from "../../components/container/ContainerContext";
import { asDropTarget } from "../../components/dragAndDrop/DroppableComponent";
import EmptyState from "../../components/emptyState";
import { withScreenContext } from "../../components/screen/ScreenContext";
import { withScrollPositionContext } from "../../components/scrollable/ScrollPositionContext";
import withScrollableView from "../../components/scrollable/withScrollableView";
import { DragDropTypes } from "../../constants/dragDropTypes";
import { NARRATIVE_VIEW } from "../../constants/identifiers";
import interfaceMessages from "../../constants/interfaceMessages";
import orientations from "../../constants/orientations";
import PubSubTopics from "../../constants/pubSubTopics";
import Dimensions, { scalePixelsToFontSize } from "../../constants/styles/dimensions";
import tableDropAction from "../../constants/tableDropAction";
import { immutableRecords, PropTypeShapes } from "../../types";
import { selectByPubSubTopics } from "../../types/immutableRecords/model/appState/utils";
import { disableWhenIos } from "../../utils/codeSplit";
import { isDragMeasuringTimespan } from "../../utils/dropFunctions";
import hasAncestorOfClass from "../../utils/hasAncestorOfClass";
import NarrativeAddNodeView from "./NarrativeAddNodeView";
import { onDropCard, onHoverCard } from "./narrativeCardDropHandler";
import NarrativeCardView from "./narrativeCardView";
import { globalRectForCardWithId } from "./utils";

// import debugUtils from "../../utils/debugUtils";

const {
  NARRATIVE_CARD_CHILD_X_MARGIN,
  NARRATIVE_CARD_CHILD_Y_MARGIN,
  NARRATIVE_CARD_FILTER_DROP_ADJUSTMENT,
  NARRATIVE_CARD_LINE_DROP_WIDTH,
  NARRATIVE_CARD_WIDTH
} = Dimensions;

const canDropOnComponent = (props, dragItem) => {
  if (isDragMeasuringTimespan(dragItem)) {
    return false;
  }
  const canDropItems = props.requestCanDropItems(dragItem.multipleItemIds);
  return canDropItems.canDrop;
};

const dropCallbacks = {
  onEnter(_props, _dragItem, _dropTarget) {
    // Do nothing
  },

  onLeave(props, _dragItem, _dropTarget) {
    if (props.onInternalDragLeave) {
      props.onInternalDragLeave();
    }
  },

  onHover(props, dragItem, positions, dropTarget) {
    if (isDragMeasuringTimespan(dragItem)) {
      return {
        message: interfaceMessages.DRAG_DROP_MEASURE_ACROSS_VIEWS_NOT_ALLOWED,
        messageType: "error"
      };
    }

    const canDropItems = props.requestCanDropItems(dragItem.multipleItemIds);
    return onHoverCard(props, dragItem, positions, dropTarget.node, canDropItems);
  },

  onDrop(props, dragItem, positions, dropTarget) {
    const canDrop = canDropOnComponent(props, dragItem);
    if (canDrop) {
      onDropCard(props, dragItem, positions, dropTarget.node);
    }
  },

  getDragMultipleItemIds(props, dragItem, _dropTarget) {
    return props.selectedItemIds.contains(dragItem.itemId)
      ? props.sortedItemIds.filter(id => props.selectedItemIds.contains(id)).toArray()
      : [dragItem.itemId];
  },

  getDragStartParams(props, dragItem, positions, dropTarget) {
    return dropTarget.component.getDragStartParams(props, dragItem, positions, dropTarget);
  },

  getDropPreviewForItem(inProps, dragItem, _dropTarget) {
    if (dragItem.itemType === DragDropTypes.TABLE_COLUMN) {
      return;
    }
    const card = inProps.requestDragPreviewDataForItem(dragItem.itemId);
    return (
      <NarrativeCardView
        key={card.id}
        id={card.id}
        card={card}
        isPreview={true}
        selected={false}
        narrativePosition={card.position}
        narrativeViewSettings={inProps.narrativeViewSettings}
        viewId={inProps.viewId}
        allowDrag={false}
        labels={inProps.labels}
        autoEditWhenBlank={false}
        typesAllowedNarrativeChildren={inProps.typesAllowedNarrativeChildren}
        typesById={inProps.typesById}
      />
    );
  },

  showCountOnPreview(_props, _dragItem, _positions, _dropTarget) {
    return true;
  }
};

const renderNarrativeCard = (card, canvasProps, allowDrag) => {
  return (
    <NarrativeCardView
      key={card.id}
      id={card.id}
      card={card}
      isActiveView={canvasProps.isActiveView}
      selectedItemIds={canvasProps.selectedItemIds}
      selected={canvasProps.selectedItemIds.indexOf(card.id) >= 0}
      narrativePosition={card.position}
      narrativeViewSettings={canvasProps.narrativeViewSettings}
      highlightDropData={canvasProps.highlightDropData}
      viewId={canvasProps.viewId}
      viewIsDropTarget={canvasProps.isOver}
      autoDeleteWhenRemoveContainer={canvasProps.autoDeleteWhenRemoveContainer}
      onClick={canvasProps.onItemClick}
      onCreateItem={canvasProps.onCreateItem}
      onLabelChange={canvasProps.onLabelChange}
      allowDrag={allowDrag}
      availableItems={canvasProps.availableItems}
      orderedNarrativeTypes={canvasProps.orderedNarrativeTypes}
      onSelectAvailableItem={canvasProps.onSelectAvailableItem}
      labels={canvasProps.labels}
      autoEditWhenBlank={canvasProps.autoEditBlankNodes}
      typesAllowedNarrativeChildren={canvasProps.typesAllowedNarrativeChildren}
      fontSize={canvasProps.fontSize}
      onShowInspector={canvasProps.onShowInspector}
      typesById={canvasProps.typesById}
      onBecameActiveView={canvasProps.onBecameActiveView}
    />
  );
};

const renderDropLine = (cards, orientation, index, dropAction, fontSize) => {
  const lineWidth = NARRATIVE_CARD_LINE_DROP_WIDTH;
  const SCALED_NARRATIVE_CARD_FILTER_DROP_ADJUSTMENT = scalePixelsToFontSize(
    NARRATIVE_CARD_FILTER_DROP_ADJUSTMENT,
    fontSize
  );

  // get the card we are placing before
  const childCard = cards.get(index);

  let lineLength;
  let x = 0;
  let y = 0;
  const adjustDropLineForFilteredItems = dropAction === tableDropAction.DropAboveRow;

  if (cards.size > 0) {
    if (orientation === orientations.HORIZONTAL) {
      x = childCard
        ? childCard.rect.left - NARRATIVE_CARD_CHILD_X_MARGIN / 2 - NARRATIVE_CARD_LINE_DROP_WIDTH
        : cards.last().rect.right + NARRATIVE_CARD_CHILD_X_MARGIN / 2;
      y = NARRATIVE_CARD_CHILD_Y_MARGIN;
      const doubleMargin = NARRATIVE_CARD_CHILD_Y_MARGIN * 2;
      lineLength = `calc(100% - ${doubleMargin}px)`;
      if (adjustDropLineForFilteredItems) {
        x += SCALED_NARRATIVE_CARD_FILTER_DROP_ADJUSTMENT;
      }
    } else {
      x = 0;
      y = childCard
        ? childCard.rect.top - NARRATIVE_CARD_CHILD_Y_MARGIN / 2 - NARRATIVE_CARD_LINE_DROP_WIDTH
        : cards.last().rect.bottom + NARRATIVE_CARD_CHILD_Y_MARGIN / 2;
      lineLength = "100%";
      if (adjustDropLineForFilteredItems) {
        y += SCALED_NARRATIVE_CARD_FILTER_DROP_ADJUSTMENT;
      }
    }
  } else {
    x = NARRATIVE_CARD_CHILD_X_MARGIN;
    y = NARRATIVE_CARD_CHILD_Y_MARGIN;
  }

  const style = {
    width: orientation === orientations.HORIZONTAL ? lineWidth : lineLength,
    height: orientation === orientations.HORIZONTAL ? lineLength : lineWidth,
    left: `${x}px`,
    top: `${y}px`
  };

  return <div key="dropIndicator" className="narrativeCardDropIndicator" style={style} />;
};

class NarrativeCanvasView extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this.state = {
      scrollLeft: 0,
      scrollTop: 0,
      dimensions: {
        width: -1,
        height: -1
      }
    };
    this.onScrollToItemReceived = this.onScrollToItemReceived.bind(this);
    this.onSelectItemByTopicsReceived = this.onSelectItemByTopicsReceived.bind(this);
    this.onDoubleClick = this.onDoubleClick.bind(this);
    this.getDragStartParams = this.getDragStartParams.bind(this);
    this.onCreateItem = this.onCreateItem.bind(this);
    this.onSelectAvailableItem = this.onSelectAvailableItem.bind(this);
    this._addNode = null;
  }

  componentDidMount() {
    this.props.registerDropTarget(this, dropCallbacks);
    this._pubSubScrollToItemToken = PubSub.subscribe(PubSubTopics.scrollToItem, this.onScrollToItemReceived);
    this._pubSubSelectItemByTopicsToken = PubSub.subscribe(
      PubSubTopics.selectItemByTopics,
      this.onSelectItemByTopicsReceived
    );
  }

  // componentWillReceiveProps(nextProps) {
  //   debugUtils.logPropChanges("NarrativeCanvasView", this.props, nextProps, true);
  // }

  componentWillUnmount() {
    PubSub.unsubscribe(this._pubSubScrollToItemToken);
    PubSub.unsubscribe(this._pubSubSelectItemByTopicsToken);
  }

  onScrollToItemReceived(msg, data) {
    this.scrollToItem(data.id);
  }

  scrollToItem(itemId) {
    const globalRect = globalRectForCardWithId(this.props.displayData.cards, itemId);
    if (globalRect) {
      this.props.xScrollActions.animateRectIntoView(globalRect);
      this.props.yScrollActions.animateRectIntoView(globalRect);
    }
  }

  getItemsListByCardsDepthFirst(cards) {
    return cards.reduce((idList, card) => {
      return idList.push(card).concat(this.getItemsListByCardsDepthFirst(card.cards));
    }, List());
  }

  onSelectItemByTopicsReceived(msg, selectTopic) {
    const newSelectedIds = selectByPubSubTopics(
      this.props.isActiveView,
      this.getItemsListByCardsDepthFirst(this.props.displayData.cards).map(card => card.itemDisplay),
      selectTopic,
      this.props.selectedItemIds
    );
    if (newSelectedIds.length > 0) {
      this.props.onSelectItems(newSelectedIds);
      this.onScrollToItemReceived("", { id: newSelectedIds[0] });
    }
  }

  getVirtualOffset() {
    return immutableRecords.PointRecord({
      x: this.props.xScrollPosition.canvas.min,
      y: this.props.yScrollPosition.canvas.min
    });
  }

  getCanvasSize() {
    return immutableRecords.PointRecord({
      width: this.props.xScrollPosition.canvas.range,
      height: this.props.yScrollPosition.canvas.range
    });
  }

  onCreateItem(typeId) {
    const cards = this.props.displayData.cards;
    this.props.onCreateItem(null, cards.size, typeId);
  }

  onSelectAvailableItem(option) {
    const cards = this.props.displayData.cards;
    const position = cards.size ? cards.last().position.first() : 0;
    this.props.onSelectAvailableItem(option, null, position);
  }

  renderAddNode() {
    const narrativeAddNodeId = `narrativeAddNode-${this.props.viewId}`;
    const cards = this.props.displayData.cards;
    let left = cards.size ? cards.last().rect.left + cards.last().rect.width + 20 : 20;
    let top = 10;

    // If it's vertical, move add node to the bottom
    if (this.props.narrativeViewSettings.columnLevel < 0) {
      left = 20;
      top = cards.size ? cards.last().rect.top + cards.last().rect.height + 10 : 10;
    }

    return (
      <NarrativeAddNodeView
        x={left}
        y={top}
        width={NARRATIVE_CARD_WIDTH}
        onClick={this.onCreateItem}
        availableItems={this.props.availableItems}
        orderedNarrativeTypes={this.props.orderedNarrativeTypes}
        id={narrativeAddNodeId}
        onSelectAvailableItem={this.onSelectAvailableItem}
        onCreateItem={this.onCreateItem}
        label="Add"
        narrativeViewSettings={this.props.narrativeViewSettings}
        depth={0}
        ref={node => (this._addNode = node)}
      />
    );
  }

  onDoubleClick(e) {
    if (hasAncestorOfClass(e.target, "narrativeCard")) {
      return;
    }

    // Open add menu on double click
    if (this._addNode) {
      const domNode = this._addNode.props ? document.getElementById(this._addNode.props.id) : this._addNode;
      if (domNode) {
        domNode.click();
        domNode.scrollIntoViewIfNeeded
          ? domNode.scrollIntoViewIfNeeded({ behavior: "smooth" })
          : domNode.scrollIntoView({ behavior: "smooth" });
      }
    }
  }

  getDragStartParams(_props, _dragItem, positions, _dropTarget) {
    const screenPointerPos = _.clone(positions.screenPointerPos);
    const sourceRectPointerPosX = screenPointerPos.x - positions.sourceRect.left;
    const sourceRectPointerPosY = screenPointerPos.y - positions.sourceRect.top;

    return {
      sourceRectPointerPosX,
      sourceRectPointerPosY
    };
  }

  render() {
    const cards = this.props.displayData.cards;
    const canvasSize = this.getCanvasSize();
    const style = {
      width: `${canvasSize.width}px`,
      height: `${canvasSize.height}px`
    };
    const dropData = this.props.highlightDropData;

    let drawDropLine = false;

    let allowDrag = true;
    //#!if iOS === true
// allowDrag = this.props.screenContext.isEditMode;
    //#!endif

    if (dropData) {
      drawDropLine = _.isNil(dropData.targetParent);
    }

    const childNodes = cards
      .map(card => {
        return renderNarrativeCard(card, this.props, allowDrag);
      })
      .toArray();

    if (drawDropLine) {
      childNodes.push(
        renderDropLine(
          cards,
          this.props.displayData.orientation,
          dropData.targetIndex,
          dropData.dropAction,
          this.props.fontSize
        )
      );
    }

    let emptyState = null;
    if (childNodes.length === 0) {
      const isFiltered = this.props.isViewFilterActive || this.props.isGlobalFilterActive;
      //#!if readonly === true
      emptyState = (
        <EmptyState type="text" absolute={true}>
          <div key="content">
            <p>No items to display{isFiltered ? " for this filter" : ""}.</p>
          </div>
        </EmptyState>
      );
      //#!else
// emptyState = <EmptyState view={NARRATIVE_VIEW} type="view" absolute={true} isFiltered={isFiltered} />;
      //#!endif
    }

    return (
      <div
        className="narrativeViewCanvas"
        style={style}
        ref={node => (this._domNode = node)}
        onDoubleClick={disableWhenIos(this.onDoubleClick)}
      >
        {emptyState}
        {childNodes}

        {/* #!if readonly !== true */}
{/* {this.renderAddNode()}*/}
        {/* #!endif */}
      </div>
    );
  }
}

NarrativeCanvasView.propTypes = {
  fontSize: PropTypes.number,

  viewId: PropTypes.string.isRequired,
  isActiveView: PropTypes.bool.isRequired,
  localStore: PropTypes.object.isRequired, // Needed for onHoverCard callback
  displayData: PropTypeShapes.narrativeData.isRequired,
  sortedItemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  narrativeIndexById: ImmutablePropTypes.map.isRequired,
  selectedItemIds: ImmutablePropTypes.listOf(PropTypes.string).isRequired,
  highlightDropData: PropTypes.instanceOf(immutableRecords.NarrativeDropPositionRecord),
  narrativeViewSettings: PropTypeShapes.narrativeViewSettings.isRequired,

  typesAllowedNarrativeChildren: ImmutablePropTypes.listOf(PropTypes.string).isRequired,

  onDispatchLastMoveAction: PropTypes.func.isRequired,
  onInternalMoveCard: PropTypes.func.isRequired,
  onItemClick: PropTypes.func.isRequired,
  onSelectItems: PropTypes.func.isRequired,
  onCreateItem: PropTypes.func.isRequired,
  onLabelChange: PropTypes.func,
  requestDragPreviewDataForItem: PropTypes.func.isRequired,
  requestCanDropItems: PropTypes.func.isRequired,
  autoEditBlankNodes: PropTypes.bool.isRequired,
  onShowInspector: PropTypes.func,

  typesById: ImmutablePropTypes.map.isRequired,

  // From asDropTarget
  registerDropTarget: PropTypes.func.isRequired,

  // Also accessed by ScrollableView
  requestViewBoundsForZoom: PropTypes.func.isRequired,

  // From withScrollPositionContext
  xScrollPosition: PropTypes.instanceOf(immutableRecords.ScrollPositionRecord).isRequired,
  xScrollActions: PropTypeShapes.scrollActions.isRequired,
  yScrollPosition: PropTypes.instanceOf(immutableRecords.ScrollPositionRecord).isRequired,
  yScrollActions: PropTypeShapes.scrollActions.isRequired,

  // From withContainerContext
  containerRect: PropTypeShapes.flatRect.isRequired,

  //#!if iOS === true
  // From withScreenContext
// screenContext: PropTypes.object,
  //#!endif

  availableItems: ImmutablePropTypes.list,
  onSelectAvailableItem: PropTypes.func,
  orderedNarrativeTypes: ImmutablePropTypes.list,
  labels: ImmutablePropTypes.list,
  isViewFilterActive: PropTypes.bool,
  isGlobalFilterActive: PropTypes.bool,
  onBecameActiveView: PropTypes.func.isRequired
};

const scrollableViewOptions = {
  scrollHorizontally: false,
  scrollVertically: true,
  showHorizontalScrollbar: false,
  showVerticalScrollbar: true
};

const normalView = withContainerContext(
  withScrollableView(
    withScrollPositionContext(asDropTarget(NarrativeCanvasView, NARRATIVE_VIEW)),
    scrollableViewOptions
  )
);

let exportedView = normalView;
//#!if iOS === true
// exportedView = withScreenContext(normalView);
//#!endif

export default exportedView;
